import dayjs from 'dayjs';

import request from '../../utils/Request';
import { Methods, NotificationType } from '../../config';
import { downloadFile } from '../../utils/download';
import { ResponseType } from '../../config/responetype';
import addNotification from '../../utils/addNotification';

const baseURL = 'resourceOne';

const resourceOneService = {
  getResourcesByGroupId: (data, resourceId) => {
    const url = resourceId
      ? `${baseURL}/resources/group/${encodeURIComponent(resourceId)}`
      : `${baseURL}/resources/group`;

    return request({
      method: Methods.POST,
      resource: url,
      data,
    });
  },

  getResourcesByGroupIdView: (data) => {
    return request({
      method: Methods.POST,
      resource: `${baseURL}/group/view`,
      data,
    });
  },

  getResourceById: (id) => {
    return request({
      method: Methods.GET,
      resource: `${baseURL}/resource/${encodeURIComponent(id)}`,
    });
  },

  getResources: (data) => {
    return request({
      method: Methods.POST,
      resource: `${baseURL}/resources`,
      data,
    });
  },

  checkConsistency: (data) => {
    return request({
      method: Methods.POST,
      resource: `${baseURL}/consistencyCheck`,
      data,
    });
  },

  createResource: (data) => {
    return request({
      method: Methods.POST,
      resource: `${baseURL}`,
      data,
    });
  },

  updateResource: (data, id) => {
    return request({
      method: Methods.PUT,
      resource: `${baseURL}/${encodeURIComponent(id)}`,
      data,
    });
  },

  deleteResource: (data, id) => {
    return request({
      method: Methods.DELETE,
      resource: `${baseURL}/${encodeURIComponent(id)}`,
      data,
    });
  },

  orderResource: (id, data) => {
    return request({
      method: Methods.POST,
      resource: `${baseURL}/orderResource/${encodeURIComponent(id)}`,
      data,
    });
  },

  addCommunicationToList: (data, id) => {
    return request({
      method: Methods.POST,
      resource: `${baseURL}/resourceOneCommunication/${encodeURIComponent(id)}`,
      data,
    });
  },

  deleteCommunicationFromList: (data, id) => {
    return request({
      method: Methods.DELETE,
      resource: `${baseURL}/resourceOneCommunication/${encodeURIComponent(id)}`,
      data,
    });
  },

  updateCommunication: (data, id) => {
    return request({
      method: Methods.PUT,
      resource: `${baseURL}/resourceOneCommunication/${encodeURIComponent(id)}`,
      data,
    });
  },

  getClassificationBySubMenuId: (subMenuId) => {
    return request({
      method: Methods.POST,
      resource: `${baseURL}/classificationItems/${encodeURIComponent(subMenuId)}`,
    });
  },

  getExcelTemplate: async () => {
    const excelFromServer = await request({
      responseType: 'blob',
      resource: `${baseURL}/exceltemplate`,
    });
    return downloadFile(excelFromServer, `Eroforras_1_template-${dayjs().format('YYYYMMDD_HHmm')}.xlsx`);
  },

  getExcelData: async () => {
    const excelFromServer = await request({
      responseType: 'blob',
      resource: `${baseURL}/exceldata`,
    });
    return downloadFile(excelFromServer, `Eroforras_1-${dayjs().format('YYYYMMDD_HHmm')}.xlsx`);
  },

  importExcel: async (files, userId) => {
    const formData = new FormData();

    for (const file of files) {
      formData.append('files', file);
    }
    formData.append('userId', userId);

    const resp = await request({
      method: Methods.POST,
      resource: `${baseURL}/excelimport`,
      formData: true,
      data: formData,
      responseType: 'blob',
    });

    if (resp?.type === ResponseType.xlsx) {
      addNotification({
        content: 'Az import sikertelen!',
        type: NotificationType.ERROR,
      });

      downloadFile(resp, `ERR_${files[0].name}`);
      return Promise.reject(new Error('Error in uploaded file!'));
    }

    const reader = new FileReader();
    reader.readAsText(resp);
    reader.onload = () => {
      const jsonData = JSON.parse(reader.result);
      if (jsonData?.error?.message) {
        return addNotification({
          content: jsonData.error.message,
          type: NotificationType.ERROR,
        });
      }
      return jsonData.data;
    };
  },

  uploadFile: (file, id, userId) => {
    const formData = new FormData();
    formData.append('files', file);
    formData.append('userId', userId);

    return request({
      method: Methods.POST,
      resource: `${baseURL}/uploadFile/${encodeURIComponent(id)}`,
      formData: true,
      data: formData,
    });
  },

  deleteFile: (id, userId) => {
    return request({
      method: Methods.DELETE,
      resource: `${baseURL}/deleteFile/${encodeURIComponent(id)}`,
      data: { userId },
    });
  },

  getAbsenceForResourceOne: (id) => {
    return request({
      method: Methods.GET,
      resource: `${baseURL}/absenceForResourceOne/${encodeURIComponent(id)}`,
    });
  },
};

export default resourceOneService;
